/**
 * Deterministischer Zufallsgenerator.
 *
 * Jeder Zufall im Spiel laeuft hier durch — nie `Math.random()`. Damit ist ein
 * kompletter Durchlauf allein aus dem Seed reproduzierbar, was Voraussetzung
 * fuer den Mystery-Kohaerenz-Fuzztest und fuer nachstellbare Bugreports ist.
 *
 * Kernidee `derive()`: Jedes Subsystem zieht seinen eigenen benannten Substream.
 * Ohne das wuerde eine zusaetzliche Zufallsziehung in der Missionslogik saemtliche
 * spaeteren Ziehungen verschieben und bei gleichem Seed eine voellig andere Welt
 * erzeugen. Mit benannten Substreams bleiben unbeteiligte Systeme stabil.
 */

/** Erzeugt aus einem String vier gut durchmischte 32-Bit-Startwerte (cyrb128). */
function hashSeed(input: string): [number, number, number, number] {
  let h1 = 1779033703;
  let h2 = 3144134277;
  let h3 = 1013904242;
  let h4 = 2773480762;

  for (let i = 0; i < input.length; i++) {
    const k = input.charCodeAt(i);
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
  }

  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);

  return [(h1 ^ h2 ^ h3 ^ h4) >>> 0, (h2 ^ h1) >>> 0, (h3 ^ h1) >>> 0, (h4 ^ h1) >>> 0];
}

export class Rng {
  private a: number;
  private b: number;
  private c: number;
  private d: number;

  /** Zwischengespeicherter zweiter Wert aus Box-Muller. */
  private spareGaussian: number | null = null;

  /** Der Seed-String, aus dem dieser Stream hervorgegangen ist. */
  readonly seed: string;

  private constructor(seed: string) {
    this.seed = seed;
    const [a, b, c, d] = hashSeed(seed);
    this.a = a;
    this.b = b;
    this.c = c;
    this.d = d;

    // Die ersten Ausgaben eines frisch geseedeten sfc32 korrelieren noch mit dem
    // Startzustand. Ein kurzer Vorlauf mischt das weg.
    for (let i = 0; i < 12; i++) this.next();
  }

  static fromSeed(seed: string | number): Rng {
    return new Rng(String(seed));
  }

  /**
   * Liefert einen unabhaengigen Substream. Gleicher Name + gleicher Elternseed
   * ergibt immer denselben Stream, unabhaengig davon, wie oft der Elternstream
   * zwischenzeitlich benutzt wurde.
   */
  derive(label: string): Rng {
    return new Rng(`${this.seed}::${label}`);
  }

  /** Gleichverteilt in [0, 1). */
  next(): number {
    this.a |= 0;
    this.b |= 0;
    this.c |= 0;
    this.d |= 0;

    const t = (((this.a + this.b) | 0) + this.d) | 0;
    this.d = (this.d + 1) | 0;
    this.a = this.b ^ (this.b >>> 9);
    this.b = (this.c + (this.c << 3)) | 0;
    this.c = (this.c << 21) | (this.c >>> 11);
    this.c = (this.c + t) | 0;

    return (t >>> 0) / 4294967296;
  }

  /** Ganzzahl in [min, max). */
  int(min: number, max: number): number {
    if (max <= min) return min;
    return min + Math.floor(this.next() * (max - min));
  }

  /** Ganzzahl in [min, max]. */
  intInclusive(min: number, max: number): number {
    return this.int(min, max + 1);
  }

  /** Fliesskomma in [min, max). */
  float(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  /** `true` mit der angegebenen Wahrscheinlichkeit. */
  bool(probability = 0.5): boolean {
    return this.next() < probability;
  }

  /** Ein zufaelliges Element. Wirft bei leerer Liste — das ist immer ein Logikfehler. */
  pick<T>(items: readonly T[]): T {
    if (items.length === 0) {
      throw new Error('Rng.pick: leere Liste');
    }
    return items[this.int(0, items.length)] as T;
  }

  /**
   * Gewichtete Auswahl. Eintraege mit Gewicht <= 0 werden uebersprungen.
   * Zentral fuer die Hinweisverteilung, wo manche Fundstuecke seltener sein sollen.
   */
  pickWeighted<T>(items: readonly T[], weightOf: (item: T) => number): T {
    if (items.length === 0) {
      throw new Error('Rng.pickWeighted: leere Liste');
    }

    let total = 0;
    for (const item of items) {
      const w = weightOf(item);
      if (w > 0) total += w;
    }

    if (total <= 0) {
      throw new Error('Rng.pickWeighted: alle Gewichte sind 0 oder negativ');
    }

    let roll = this.next() * total;
    for (const item of items) {
      const w = weightOf(item);
      if (w <= 0) continue;
      roll -= w;
      if (roll < 0) return item;
    }

    // Nur durch Fliesskomma-Rundung erreichbar.
    return items[items.length - 1] as T;
  }

  /** Neue, gemischte Kopie (Fisher-Yates). Die Eingabe bleibt unveraendert. */
  shuffle<T>(items: readonly T[]): T[] {
    const out = [...items];
    for (let i = out.length - 1; i > 0; i--) {
      const j = this.int(0, i + 1);
      const tmp = out[i] as T;
      out[i] = out[j] as T;
      out[j] = tmp;
    }
    return out;
  }

  /** `count` verschiedene Elemente. Kuerzt still, wenn die Liste zu klein ist. */
  sample<T>(items: readonly T[], count: number): T[] {
    return this.shuffle(items).slice(0, Math.max(0, count));
  }

  /**
   * Normalverteilt (Box-Muller). Fuer Wetter, Zeitfenster-Streuung und alles,
   * wo Gleichverteilung unnatuerlich wirkt.
   */
  gaussian(mean = 0, stdDev = 1): number {
    if (this.spareGaussian !== null) {
      const spare = this.spareGaussian;
      this.spareGaussian = null;
      return mean + stdDev * spare;
    }

    let u = 0;
    let v = 0;
    let s = 0;
    do {
      u = this.next() * 2 - 1;
      v = this.next() * 2 - 1;
      s = u * u + v * v;
    } while (s >= 1 || s === 0);

    const factor = Math.sqrt((-2 * Math.log(s)) / s);
    this.spareGaussian = v * factor;
    return mean + stdDev * u * factor;
  }

  /** Normalverteilt, aber hart auf [min, max] begrenzt. */
  gaussianClamped(mean: number, stdDev: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, this.gaussian(mean, stdDev)));
  }
}

const SEED_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // ohne I/O/0/1 — leichter vorzulesen

/**
 * Erzeugt einen kurzen, gut kommunizierbaren Seed im Format `ABCD-EFGH`.
 * Nutzt bewusst `Math.random()`: das ist der einzige Ort, an dem echter
 * Zufall erwuenscht ist, naemlich beim Start eines neuen Spiels.
 */
export function generateSeed(): string {
  let out = '';
  for (let i = 0; i < 8; i++) {
    if (i === 4) out += '-';
    out += SEED_ALPHABET[Math.floor(Math.random() * SEED_ALPHABET.length)];
  }
  return out;
}
